import { Dialog, Transition } from "@headlessui/react";
import React, { FormEvent, Fragment, ReactNode } from "react";
import { FiX } from "react-icons/fi";

export type ModalSize = "sm" | "md" | "lg" | "xl";

const modalSizeClasses: Record<ModalSize, string> = {
  sm: "w-[580px]",
  md: "w-[720px]",
  lg: "w-[880px]",
  xl: "w-[1080px]",
};

export interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  submitButtonTitle?: string;
  cancelButtonTitle?: string;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  footerDisabled?: boolean;
  size?: ModalSize;
  children: ReactNode;
}

export default function FormModal({
  isOpen,
  onClose,
  title,
  description,
  submitButtonTitle = "Criar conta",
  cancelButtonTitle = "Cancelar",
  onSubmit,
  isSubmitting = false,
  submitDisabled = false,
  footerDisabled = false,
  size = "md",
  children,
}: FormModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#101828]/60 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-10 max-[576px]:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`relative z-10 flex max-h-[calc(100dvh-80px)] max-w-[calc(100vw-80px)] flex-col overflow-hidden rounded-[32px] bg-white shadow-[2px_2px_18px_0_rgba(0,0,0,0.12)] max-[576px]:max-h-dvh max-[576px]:w-full max-[576px]:max-w-full ${modalSizeClasses[size]}`}
              >
                {/* Header */}
                <div className="shrink-0 bg-[#125247] p-10 text-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Dialog.Title className="font-['Inter'] text-[32px] font-bold leading-[140%] text-white">
                        {title}
                      </Dialog.Title>
                      {description && (
                        <div className="mt-2 font-['Inter'] text-base text-grey300">
                          {description}
                        </div>
                      )}
                    </div>

                    {/* Close button on desktop */}
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Fechar modal"
                      className="flex shrink-0 items-center justify-center rounded-full bg-black p-4 text-white transition-opacity hover:opacity-80"
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                </div>

                {/* Form and Body */}
                <form
                  className="flex min-h-0 flex-1 flex-col"
                  onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit?.(e);
                  }}
                >
                  {/* Content with responsive padding */}
                  <div className="min-h-0 flex-1 overflow-y-auto p-10">{children}</div>

                  {/* Footer */}
                  {!footerDisabled && (
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-grey100 bg-white px-10 py-6">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="flex h-10 w-fit items-center rounded-full px-5 text-sm font-bold text-black transition-colors hover:bg-gray-100 disabled:opacity-50"
                      >
                        {cancelButtonTitle}
                      </button>
                      <button
                        type="submit"
                        disabled={submitDisabled || isSubmitting}
                        className="h-10 w-fit rounded-full bg-black px-6 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                      >
                        {isSubmitting ? "Carregando..." : submitButtonTitle}
                      </button>
                    </div>
                  )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
